import React, { useCallback, useMemo, useState } from "react";
import { MockedProvider } from "@apollo/client/testing/react";
import type { Meta, StoryObj } from "@storybook/react";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import SignupSigninStack, { SignupSigninScreens } from "../../../navigations/SignupSigninStack";
import { GET_DEFAULT_PROFILE_TYPE } from "../../../graphql/identity";
import { MockedAuthManager } from "../../../contexts/AuthManager";
import { UserFieldsFragment } from "../../../__generated__/operations/graphql";
import { ConfirmationResult, UserCredential } from "@react-native-firebase/auth";
import FirebaseManager, { FirebaseStatus } from "../../../contexts/FirebaseManager";
import { StoryStackNavigatorContainer } from "../../display-helpers";

type StoryStackParams = {
    SignupSigninStack?: { isSignup?: boolean; initialRouteName?: SignupSigninScreens };
};

const Stack = createNativeStackNavigator<StoryStackParams>();

const StoryComponent = ({ isSignup = false }: { isSignup?: boolean }) => {
    const [user, setUser] = useState<UserFieldsFragment | null>();

    const mocks = useMemo(
        () => [
            {
                request: {
                    query: GET_DEFAULT_PROFILE_TYPE,
                    variables: () => true,
                },
                result: () => ({
                    data: {
                        getDefaultProfileType: null,
                    },
                }),
            },
        ],
        [],
    );

    const signInWithPhoneNumber = useCallback<
        (phoneNumber: string) => Promise<ConfirmationResult>
    >(() => {
        return Promise.resolve({
            verificationId: "",
            confirm: (code: string) => {
                switch (code) {
                    case "111111":
                        setUser(null);
                        break;
                    case "222222":
                        setUser(null);
                        break;
                    case "333333":
                        setUser(null);
                        break;
                    default:
                        setUser(null);
                        break;
                }
                return {} as Promise<UserCredential>;
            },
        });
    }, []);

    return (
        <FirebaseManager
            testContext={{
                status: FirebaseStatus.Out,
                user: undefined,
                signOut: () => Promise.resolve(),
            }}
        >
            <MockedAuthManager user={user} signInWithPhoneNumber={signInWithPhoneNumber}>
                <MockedProvider mocks={mocks}>
                    <StoryStackNavigatorContainer>
                        <Stack.Navigator initialRouteName="SignupSigninStack">
                            <Stack.Screen
                                name="SignupSigninStack"
                                component={SignupSigninStack}
                                initialParams={{ isSignup }}
                                options={{
                                    headerShown: false,
                                    gestureEnabled: false,
                                }}
                            />
                        </Stack.Navigator>
                    </StoryStackNavigatorContainer>
                </MockedProvider>
            </MockedAuthManager>
        </FirebaseManager>
    );
};

const meta = {
    title: "SignupSignin/FULL",
    component: StoryComponent,
    argTypes: {
        isSignup: {
            control: {
                type: "boolean",
            },
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Signup: Story = {
    args: {
        isSignup: true,
    },
};
export const Signin: Story = {
    args: {
        isSignup: false,
    },
};
